import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "./components/ContactForm";
import { organizationSchema, websiteSchema, breadcrumbSchema } from "@/lib/seoData";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "更新及时",
    desc: "持续同步新番索引与连载作品信息，让动漫网全部动漫列表始终鲜活。",
  },
  {
    icon: "ri-grid-line",
    title: "分类清晰",
    desc: "国漫专区、日漫检索、漫画分类大全与动画库一站配齐，检索轻松。",
  },
  {
    icon: "ri-heart-line",
    title: "热爱纯粹",
    desc: "一切出发点都是让动漫爱好者找漫画、追更新更简单、更快乐。",
  },
  {
    icon: "ri-shield-check-line",
    title: "内容可靠",
    desc: "用心整理每一部作品的资料与资讯，提供可信、易读的浏览体验。",
  },
];

const testimonials = [
  {
    quote: "终于有个地方能把国漫和日漫放在一起检索了，分类清清楚楚，找番特别快。",
    name: "星野同学",
    role: "追番 5 年",
  },
  {
    quote: "新番索引更新得很及时，每季开播前我都在动漫网先把片单过一遍。",
    name: "阿澈",
    role: "日漫爱好者",
  },
  {
    quote: "资讯和盘点文章写得很有味道，读完总能发现几部想补的宝藏作品。",
    name: "小禾",
    role: "国漫观众",
  },
];

const jsonLd = [
  organizationSchema,
  websiteSchema,
  breadcrumbSchema([
    { name: "首页", path: "/" },
    { name: "关于我们", path: "/about" },
  ]),
];

export default function About() {
  return (
    <Layout>
      <Seo
        title="关于我们 - 动漫网品牌介绍 / 口碑与联系方式"
        description="了解动漫网的品牌故事、核心理念与用户口碑，并通过我们的联系表单或联系方式与我们取得联系。"
        keywords="动漫网,关于动漫网,动漫网品牌介绍,动漫网联系方式,动漫网全部动漫列表"
        canonical="/about"
        jsonLd={jsonLd}
      />

      <section className="bg-background-100 pt-24 pb-14 md:pb-20">
        <div className="mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: "关于我们", path: "/about" }]} />
          <div className="mt-4 max-w-3xl">
            <span className="font-heading text-sm text-primary-600">关于动漫网</span>
            <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">让每一位动漫爱好者，都能轻松找到心头好</h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              动漫网是一个面向中文用户的动漫资源聚合检索门户。我们把新番索引、漫画分类大全、国漫专区、日漫检索与动画库集合在一个入口，
              希望帮每一位热爱动漫的人，少花时间在翻找上，多花时间在享受故事里。
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="font-heading text-sm text-primary-600">品牌介绍</span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">我们为什么做动漫网</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                动漫的世界辽阔而迷人，但作品越多，寻找反而越难。动漫网应运而生，我们专注做一件事：
                把国漫与日漫、新番与经典、热血与治愈，都清晰地装进一个入口。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                无论是想第一时间追新番，还是想按分类细细挑选，或是随手看看资讯与盘点，
                动漫网都希望成为你值得信赖的动漫好伙伴。
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <p className="font-heading text-3xl text-primary-600">10+</p>
                  <p className="mt-1 text-xs text-foreground-600">动漫分类</p>
                </div>
                <div>
                  <p className="font-heading text-3xl text-primary-600">1200+</p>
                  <p className="mt-1 text-xs text-foreground-600">收录动漫</p>
                </div>
                <div>
                  <p className="font-heading text-3xl text-accent-500">100%</p>
                  <p className="mt-1 text-xs text-foreground-600">专注动漫</p>
                </div>
                <div>
                  <p className="font-heading text-3xl text-secondary-500">4.9</p>
                  <p className="mt-1 text-xs text-foreground-600">用户评分</p>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-background-200/70">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20warm%20illustration%20of%20a%20cozy%20anime%20fan%20workspace%20with%20posters%20figurines%20and%20a%20laptop%2C%20orange%20and%20amber%20palette%2C%20soft%20window%20light%2C%20inviting%20atmosphere&width=1200&height=800&seq=about-team-2026&orientation=landscape"
                alt="动漫网工作台插画"
                title="关于动漫网"
                className="aspect-[3/2] w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="font-heading text-sm text-primary-600">核心理念</span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">我们坚持的价值观</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="font-heading text-sm text-primary-600">用户口碑</span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">来自同好的认可</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <div className="flex gap-0.5 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-800">“{t.quote}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-semibold text-secondary-900">
                    {t.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
            <div>
              <span className="font-heading text-sm text-primary-600">联系方式</span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">与我们保持联系</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                无论是建议、反馈还是合作意向，都欢迎通过下面的表单或联系方式告诉我们。
                我们会认真阅读每一条留言。
              </p>
              <ul className="mt-6 space-y-4 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">邮箱</p>
                    <p className="whitespace-nowrap">contact@anime.readdy.site</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">服务时间</p>
                    <p>客服在线 9:00 - 22:00</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                    <i className="ri-global-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">平台</p>
                    <p>动漫网 · 线上服务平台</p>
                  </div>
                </li>
              </ul>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </Layout>
  );
}